"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, MessageCircle, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const faqData = [{
  question: "What is Technical SEO?",
  answer: "Technical SEO is the practice of optimising the infrastructure, code, and server configurations of a website so search engines can discover, crawl, render, index, and understand its content without friction. While content and backlinks build relevance and authority, technical SEO ensures search engines can actually access and evaluate that value.",
  jargon: "Infrastructure & crawl efficiency"
}, {
  question: "What does a Technical SEO consultant do?",
  answer: "As an independent technical SEO consultant, I diagnose architectural barriers that prevent search engines from indexing and ranking your pages properly. I audit server responses, crawl efficiency, rendering pipelines, internal linking hierarchies, structured data, and Core Web Vitals, then deliver prioritized, developer-ready fixes that your engineering team or agency can implement immediately.",
  jargon: "Hands-on audit & engineering guidance"
}, {
  question: "Why is Technical SEO important?",
  answer: "Even the highest-quality content and strongest backlink profile cannot generate search traffic if search bots encounter crawl blocks, rendering failures, canonical conflicts, or slow server responses. Technical SEO protects your investment in organic search by eliminating structural bottlenecks before they degrade your visibility.",
  jargon: "The foundation for organic performance"
}, {
  question: "What does a Technical SEO audit involve?",
  answer: "A comprehensive audit evaluates your site across multiple technical layers: server health and response codes, robots directives, XML sitemaps, rendering performance (especially for JavaScript frameworks), URL architecture, canonicalization, indexation coverage in Google Search Console, mobile usability, Core Web Vitals, and structured data validation.",
  jargon: "End-to-end architectural evaluation"
}, {
  question: "How do I know if my website has technical SEO problems?",
  answer: "Common symptoms include organic traffic flatlining despite publishing high-quality content, sudden drops following a site redesign or platform migration, pages lingering in Google Search Console under 'Discovered – currently not indexed' or 'Crawled – currently not indexed', persistent Core Web Vitals failures, or significant discrepancies between indexed URLs and your actual product or article count.",
  jargon: "Traffic drops, crawl stagnation, and index bloat"
}, {
  question: "What are Core Web Vitals?",
  answer: "Core Web Vitals are Google's standardised metrics for evaluating real-world user experience: Largest Contentful Paint (LCP for loading performance), Interaction to Next Paint (INP for responsiveness), and Cumulative Layout Shift (CLS for visual stability). Passing Core Web Vitals does not automatically grant top search rankings; rather, it eliminates user friction and supports wider engagement and search eligibility.",
  jargon: "LCP = Loading speed, INP = Interactivity, CLS = Visual stability"
}, {
  question: "What is crawlability?",
  answer: "Crawlability refers to how easily and efficiently search engine bots can navigate the links, paths, and assets across your website. If your internal architecture contains orphaned pages, broken redirect chains, unmanaged faceted parameters, or restrictive robots directives, search bots may waste crawl budget and fail to reach key revenue-driving pages.",
  jargon: "How search engine spiders discover your pages"
}, {
  question: "Why are some pages not being indexed by Google?",
  answer: "Pages typically remain unindexed for a combination of technical and quality reasons: restrictive directives (such as accidental 'noindex' tags or canonical misconfigurations), rendering failures where bots cannot view JavaScript-generated text, duplicate content, thin value, or crawl budget constraints where Google prioritizes higher-authority sections of your domain.",
  jargon: "Directives, quality thresholds, or render timeouts"
}, {
  question: "What is JavaScript SEO?",
  answer: "JavaScript SEO focuses on ensuring websites built with client-side frameworks—like React, Vue, Next.js, or Angular—render properly for search bots. Because search engines must execute JavaScript in a two-wave rendering pipeline, hydration delays, unrendered internal links, or missing server-rendered meta tags can cause bot indexing delays or complete content invisibility.",
  jargon: "Client-side rendering vs. server-side execution"
}, {
  question: "Does schema markup improve rankings?",
  answer: "Schema markup does not directly increase algorithmic ranking scores. What structured data does is provide unambiguous context about your entities, products, FAQs, and events. This qualifies your pages for Google Rich Results (such as review stars, recipe cards, or product availability snippets), which can substantially improve click-through rates from search results.",
  jargon: "Rich results qualification without direct ranking manipulation"
}, {
  question: "How long does Technical SEO take to show results?",
  answer: "The timeline depends on the nature of the issue. Urgent fixes like lifting accidental robots.txt disallows, correcting canonical loops, or fixing broken server headers can prompt Google to re-evaluate pages within days. Conversely, broader structural overhauls, site migration recoveries, and crawl budget optimizations typically take several weeks to months as Google gradually re-crawls and recalculates site-wide signals.",
  jargon: "Immediate crawl fixes vs. gradual structural re-crawling"
}, {
  question: "How often should Technical SEO be reviewed?",
  answer: "I recommend conducting a comprehensive technical audit once per year or ahead of major business milestones—such as a CMS migration, site redesign, or brand merger. In addition to periodic deep-dive audits, continuous monitoring of Search Console errors, sitemaps, and server logs ensures small codebase regressions do not compound into traffic loss.",
  jargon: "Annual deep dive alongside ongoing monitoring"
}, {
  question: "Can you work with my existing developer or agency?",
  answer: "Yes, this is my standard working model. Rather than handing over generic 80-page automated PDF reports, I translate audit findings into concise, developer-ready tickets with exact reproduction steps, rationale, code snippets, and clear acceptance criteria so your engineering team can deploy fixes smoothly.",
  jargon: "Developer-ready tickets and acceptance criteria"
}, {
  question: "Do ecommerce websites need Technical SEO?",
  answer: "Ecommerce websites are especially vulnerable to technical breakdowns due to complex faceted navigation (which generates millions of near-identical filter URLs), out-of-stock product handling, duplicate product variants, pagination, and thin category descriptions. Rigorous technical SEO protects crawl budget and directs search authority to high-intent category and product landing pages.",
  jargon: "Faceted navigation, crawl budget, and inventory controls"
}];
export default function Technicalfaq() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="technicalseofaq" data-nav="light" className="w-full bg-background text-foreground py-20 md:py-28">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          {/* Left Column: Heading & Trust Copy */}
          <motion.div className="lg:col-span-5 space-y-6" initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }}>
            <Badge variant="secondary" className="gap-1.5 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
              <HelpCircle className="size-3.5 text-primary" />
              Technical SEO Answers
            </Badge>

            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground font-serif">
              Clear answers to complex technical search questions.
            </h2>

            <p className="text-base text-muted-foreground leading-relaxed">
              Technical SEO can feel overly complex and full of industry jargon. Here are straightforward explanations to the questions I address most frequently when working directly with clients.
            </p>

            <div className="pt-2 space-y-3">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                <p className="text-sm text-foreground font-medium">
                  Practical, developer-ready recommendations
                </p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                <p className="text-sm text-foreground font-medium">
                  Practical code & infrastructure recommendations
                </p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                <p className="text-sm text-foreground font-medium">
                  Tailored to your specific tech stack & business goals
                </p>
              </div>
            </div>

            {/* Direct Consultation Callout Card */}
            <div className="p-6 rounded-2xl bg-card border border-border shadow-xs mt-8 space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-secondary text-primary">
                  <MessageCircle className="size-5" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground">Have a specific technical issue?</h3>
                  <p className="text-xs text-muted-foreground">Ask me directly about your website structure.</p>
                </div>
              </div>
              <Button asChild className="w-full justify-between group">
                <Link to="/ContactPage">
                  <span>Book a Free Consultation</span>
                  <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Column: FAQ Accordion List */}
          <motion.div className="lg:col-span-7" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }}>
            <Accordion type="single" collapsible defaultValue="item-0" className="w-full space-y-4">
              {faqData.map((faq, i) => <motion.div key={i} data-index={i} variants={itemVariants}>
                  <AccordionItem value={`item-${i}`} className="border border-border bg-card rounded-xl px-6 py-1 transition-[border-color,box-shadow] duration-200 shadow-2xs hover:border-primary/40 data-[state=open]:border-primary data-[state=open]:shadow-sm">
                    <AccordionTrigger className="text-base font-semibold text-foreground hover:no-underline py-5 text-left">
                      <span>{faq.question}</span>
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-sm leading-relaxed pb-6 space-y-3">
                      <p>{faq.answer}</p>
                      {faq.jargon && <div className="pt-2">
                          <span className="inline-block px-2.5 py-1 text-xs font-mono font-medium rounded-md bg-muted text-muted-foreground border border-border/50">
                            Quick decoder: {faq.jargon}
                          </span>
                        </div>}
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>)}
            </Accordion>
          </motion.div>

        </div>
      </div>
    </section>;
}